import CscaSubjectPicker from '../components/CscaSubjectPicker';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, BarChart3, ChevronDown, ChevronRight, Hash, Layers, Play, X } from 'lucide-react';
import { useAuthStore } from '../store';
import { api, CscaTopicAnalysisResponse, CscaTopicMetrics } from '../utils/api';

const percent = (value: number) => `${Number(value || 0).toFixed(1)}%`;

const ProbabilityBar: React.FC<{ value: number; compact?: boolean }> = ({ value, compact = false }) => (
  <div className={`overflow-hidden rounded-full bg-indigo-100 ${compact ? 'h-1.5' : 'h-2'}`}>
    <div
      className="h-full rounded-full bg-gradient-to-r from-[#4F46E5] to-[#7C3AED]"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);

const MetricPill: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2">
    <div className="text-[11px] font-semibold text-slate-400">{label}</div>
    <div className="mt-0.5 text-sm font-extrabold text-slate-700">{value}</div>
  </div>
);

const buildApproximatePositionSummary = (
  positions: CscaTopicMetrics['question_positions'],
  paperCount: number,
) => {
  const sorted = [...(positions || [])].sort((a, b) => a.position - b.position);
  if (sorted.length === 0) return '暂无数据';
  if (sorted.length === 1 && sorted[0].paper_coverage_count === paperCount) {
    return `${sorted[0].position_label}（每套均出现）`;
  }

  const ranges: Array<{ start: number; end: number }> = [];
  sorted.forEach(item => {
    const last = ranges[ranges.length - 1];
    // “大概位置”允许中间只缺一个题号，避免把粗分类拆成大量零散位置。
    if (last && item.position <= last.end + 2) last.end = item.position;
    else ranges.push({ start: item.position, end: item.position });
  });
  return ranges.map(range => {
    const start = String(range.start).padStart(2, '0');
    const end = String(range.end).padStart(2, '0');
    return range.start === range.end ? start : `${start}–${end}`;
  }).join('、');
};

const CoarsePositionSummary: React.FC<{
  positions: CscaTopicMetrics['question_positions'];
  paperCount: number;
}> = ({ positions, paperCount }) => (
  <div className="mt-3 flex min-w-0 items-start gap-2 rounded-xl bg-indigo-50 px-3 py-2 text-sm">
    <Hash className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
    <span className="shrink-0 font-extrabold text-indigo-500">历史大概位置</span>
    <span className="min-w-0 font-black text-[#312E81]">{buildApproximatePositionSummary(positions, paperCount)}</span>
  </div>
);

const FineTopicCard: React.FC<{
  item: CscaTopicMetrics & { fine_type: string };
  paperCount: number;
  onPractice: () => void;
  isMooc?: boolean;
}> = ({ item, paperCount, onPractice, isMooc = false }) => (
  <article className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="font-extrabold text-[#312E81]">{item.fine_type}</h3>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          {isMooc ? `MOOC 题库 ${item.question_count} 题` : `历史 ${item.question_count} 题 · ${item.paper_coverage_count}/${paperCount} 套出现`}
        </p>
      </div>
      <div className="shrink-0 rounded-xl bg-indigo-50 px-3 py-2 text-center">
        <div className="text-lg font-black text-[#4F46E5]">{percent(item.within_coarse_probability_percent || 0)}</div>
        <div className="text-[10px] font-semibold text-indigo-400">粗分题目占比</div>
      </div>
    </div>

    <div className="mt-4">
      <ProbabilityBar value={item.within_coarse_probability_percent || 0} compact />
    </div>

    {isMooc ? (
      <div className="mt-4 grid grid-cols-2 gap-2">
        <MetricPill label="细分题量" value={`${item.question_count} 题`} />
        <MetricPill label="MOOC题库占比" value={percent(item.question_probability_percent)} />
      </div>
    ) : (
      <>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <MetricPill label="每套平均" value={`${item.average_per_paper} 题`} />
          <MetricPill label="历史范围" value={`${item.min_per_paper}–${item.max_per_paper} 题`} />
          <MetricPill label="试卷覆盖率" value={percent(item.paper_coverage_percent)} />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {item.per_paper.map(paper => (
            <span key={paper.paper_id} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
              {paper.title.replace('CSCA 数学 ', '')}：{paper.question_count}题
            </span>
          ))}
        </div>
      </>
    )}
    <button type="button" onClick={onPractice} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#4F46E5] px-4 py-2.5 text-sm font-extrabold text-white hover:bg-indigo-700">
      <Play className="h-4 w-4 fill-current" /> 按此细分开始做题
    </button>
  </article>
);

const CSCATopicAnalysis: React.FC<{ source?: 'past' | 'mooc'; fixedLevel?: 1 | 2 | 3 }> = ({ source = 'past', fixedLevel }) => {
  const isMooc = source === 'mooc';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const level = isMooc ? 3 : fixedLevel ?? Number(params.get('level') || 3);
  const selectLevel = useCallback((value: number) => { setParams({ level: String(value) }); setExpanded(new Set()); setPracticeTarget(null); }, [setParams]);
  const picker = !isMooc && !fixedLevel && <CscaSubjectPicker level={level} onChange={selectLevel} />;
  const backPath = fixedLevel && fixedLevel !== 3 ? `/csca/simulation/${fixedLevel}` : '/csca';
  const subjectColor = !isMooc && level === 2
    ? { link: 'text-emerald-800', hero: 'from-[#065F46] via-[#047857] to-[#0D9488] shadow-emerald-500/20', softText: 'text-emerald-100' }
    : !isMooc && level === 1
      ? { link: 'text-orange-800', hero: 'from-[#9A3412] via-[#C2410C] to-[#D97706] shadow-orange-500/20', softText: 'text-orange-100' }
      : { link: 'text-[#4F46E5]', hero: 'from-[#312E81] via-[#4338CA] to-[#7C3AED] shadow-indigo-500/20', softText: 'text-indigo-200' };
  const token = useAuthStore(state => state.token);
  const [data, setData] = useState<CscaTopicAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [practiceTarget, setPracticeTarget] = useState<{ coarse: string; fine: string | null; available: number } | null>(null);
  const [practiceCount, setPracticeCount] = useState(10);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    (isMooc ? api.getCscaMoocTopics(token, 3) : api.getCscaTopicAnalysis(token, level))
      .then(response => {
        if (!cancelled) setData(response);
      })
      .catch((requestError: Error) => {
        if (!cancelled) setError(requestError.message || '无法加载题型统计');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isMooc, level, token]);

  const toggle = (name: string) => {
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const openPractice = (coarse: string, fine: string | null, available: number) => {
    setPracticeTarget({ coarse, fine, available });
    setPracticeCount(Math.min(10, available));
  };

  const startPractice = () => {
    if (!practiceTarget) return;
    const count = Math.max(1, Math.min(practiceTarget.available, Math.floor(Number(practiceCount) || 1)));
    const params = new URLSearchParams({ coarse: practiceTarget.coarse, count: String(count), level: String(level) });
    if (practiceTarget.fine) params.set('fine', practiceTarget.fine);
    navigate(`${isMooc ? '/csca/mooc/topic-practice' : '/csca/topic-practice'}?${params.toString()}`);
  };

  if (loading) return <>{picker}<div className="py-20 text-center font-semibold text-[#312E81]">{isMooc ? '正在从 D1 加载 MOOC 分类…' : '正在统计本科目的往年试卷…'}</div></>;

  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl space-y-5">{picker}
        <Link to={backPath} className="inline-flex items-center gap-2 text-sm font-bold text-[#4F46E5]"><ArrowLeft className="h-4 w-4" /> 返回本科目</Link>
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div><div className="font-bold">题型统计加载失败</div><div className="mt-1 text-sm">{error}</div></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-10">{picker}
      <Link to={backPath} className={`inline-flex items-center gap-2 text-sm font-bold ${subjectColor.link}`}>
        <ArrowLeft className="h-4 w-4" /> {fixedLevel || isMooc ? '返回本科目' : '返回 CSCA 模式选择'}
      </Link>

      <section className={`overflow-hidden rounded-3xl bg-gradient-to-br p-6 text-white shadow-xl md:p-9 ${subjectColor.hero}`}>
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <div className={`flex items-center gap-2 text-sm font-bold ${subjectColor.softText}`}><BarChart3 className="h-5 w-5" /> CSCA {data.subject} · {isMooc ? 'MOOC 题库' : level === 2 ? '原题与冲刺复习资料统计' : '往年试卷统计'}</div>
            <h1 className="mt-3 text-3xl font-black md:text-4xl">{isMooc ? 'MOOC 分类练习' : level === 2 ? '题型与题量分布' : '题型与出题概率'}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-indigo-100 md:text-base">
              {isMooc ? '按粗分类或细分类选择模拟题和 AI 题。同类优先抽取你尚未做过的模拟题，再抽取 AI 题。' : level === 2 ? '4月与6月是同一份去重后冲刺资料拆分的练习卷。题型分布供复习参考。' : '先查看粗类型，再展开细分题型。比例根据本科目已录入原题统计，供安排复习参考。'}
            </p>
          </div>
          <div className="grid shrink-0 grid-cols-3 gap-2 text-center">
            <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur"><div className="text-2xl font-black">{isMooc ? data.topics.length : data.paper_count}</div><div className="text-xs text-indigo-200">{isMooc ? '个粗分类' : '套试卷'}</div></div>
            <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur"><div className="text-2xl font-black">{data.total_questions}</div><div className="text-xs text-indigo-200">道题</div></div>
            <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur"><div className="text-2xl font-black">{data.topics.reduce((sum, topic) => sum + topic.fine_types.length, 0)}</div><div className="text-xs text-indigo-200">个细分类</div></div>
          </div>
        </div>
      </section>

      <div className="space-y-4">
        {data.topics.map(topic => {
          const isExpanded = expanded.has(topic.coarse_type);
          return (
            <section key={topic.coarse_type} className="overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => toggle(topic.coarse_type)}
                aria-expanded={isExpanded}
                className="w-full p-5 text-left transition-colors hover:bg-indigo-50/50 md:p-6"
              >
                <div className="flex flex-col gap-4 md:flex-row md:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-100 text-[#4F46E5]"><Layers className="h-6 w-6" /></div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-black text-[#312E81] md:text-2xl">{topic.coarse_type}</h2>
                        {isExpanded ? <ChevronDown className="h-5 w-5 text-indigo-400" /> : <ChevronRight className="h-5 w-5 text-indigo-400" />}
                      </div>
                      <p className="mt-1 text-sm text-slate-500">{topic.fine_types.length} 个细分 · {isMooc ? `MOOC 题库 ${topic.question_count} 题` : `历史 ${topic.question_count} 题 · ${topic.paper_coverage_count}/${data.paper_count} 套出现`}</p>
                    </div>
                  </div>

                  <div className={`grid gap-2 ${isMooc ? 'grid-cols-2 md:w-[280px]' : 'grid-cols-3 md:w-[420px]'}`}>
                    <MetricPill label={isMooc ? 'MOOC题库占比' : level === 2 ? '资料题量占比' : '出题概率'} value={percent(topic.question_probability_percent)} />
                    {isMooc ? <MetricPill label="题库题量" value={`${topic.question_count} 题`} /> : <><MetricPill label="每套平均" value={`${topic.average_per_paper} 题`} /><MetricPill label="历史范围" value={`${topic.min_per_paper}–${topic.max_per_paper} 题`} /></>}
                  </div>
                </div>
                <div className="mt-4"><ProbabilityBar value={topic.question_probability_percent} /></div>
                {!isMooc && <CoarsePositionSummary positions={topic.question_positions} paperCount={data.paper_count} />}
              </button>

              <div className="border-t border-indigo-50 px-5 py-3 text-right md:px-6">
                <button type="button" onClick={() => openPractice(topic.coarse_type, null, topic.question_count)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#312E81] px-4 py-2.5 text-sm font-extrabold text-white hover:bg-indigo-900">
                  <Play className="h-4 w-4 fill-current" /> 按此粗分类开始做题
                </button>
              </div>

              {isExpanded && (
                <div className="border-t border-indigo-100 bg-indigo-50/40 p-4 md:p-6">
                  <div className="grid gap-4 xl:grid-cols-2">
                    {topic.fine_types.map(item => <FineTopicCard key={item.fine_type} item={item} paperCount={data.paper_count} isMooc={isMooc} onPractice={() => openPractice(topic.coarse_type, item.fine_type, item.question_count)} />)}
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>

      {data.unclassified_questions > 0 && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
          仍有 {data.unclassified_questions} 道题没有分类，统计结果尚不完整。
        </div>
      )}

      {practiceTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onClick={() => setPracticeTarget(null)}>
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onClick={event => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-bold text-[#4F46E5]">CSCA {data.subject} · {isMooc ? 'MOOC 分类练习' : '题型练习'}</div>
                <h2 className="mt-1 text-2xl font-black text-[#312E81]">{practiceTarget.fine || practiceTarget.coarse}</h2>
                {practiceTarget.fine && <p className="mt-1 text-sm text-slate-500">所属粗分类：{practiceTarget.coarse}</p>}
              </div>
              <button type="button" aria-label="关闭" onClick={() => setPracticeTarget(null)} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <label className="mt-6 block text-sm font-extrabold text-slate-700" htmlFor="csca-practice-count">本次出题量</label>
            <div className="mt-2 flex items-center gap-3">
              <input id="csca-practice-count" type="number" min={1} max={practiceTarget.available} value={practiceCount} onChange={event => setPracticeCount(Number(event.target.value))} className="min-w-0 flex-1 rounded-xl border border-indigo-200 px-4 py-3 text-lg font-bold text-[#312E81] outline-none focus:border-[#4F46E5]" />
              <span className="shrink-0 text-sm text-slate-500">最多 {practiceTarget.available} 题</span>
            </div>
            <p className="mt-3 text-xs leading-5 text-slate-500">{isMooc ? '系统按所选粗分或细分优先抽取未做过的模拟题，再抽取 AI 题。指定的 7 道新类型题列在“超纲”中。' : '仅抽取已核对题。共用材料按整组抽取，实际题数可能略多于所选题量；选择细分类时，会同时带上同一材料的其他小题。'}每题按75秒配置练习时间。</p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setPracticeTarget(null)} className="rounded-xl border border-slate-200 px-4 py-3 font-bold text-slate-600 hover:bg-slate-50">取消</button>
              <button type="button" onClick={startPractice} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4F46E5] px-4 py-3 font-extrabold text-white hover:bg-indigo-700"><Play className="h-4 w-4 fill-current" /> 开始做题</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CSCATopicAnalysis;
